import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { Absent } from '../../../chrome/measure';
import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import type { AmplificationRow } from '../api/schemas';
import { overviewPath } from '../overview/paths';
import { initials } from './format';
import { pub } from './messages';

/** Each amplifier's reach on the scale of the largest one shown, so the
 *  longest bar is always the leader and the others read against it. */
export function AmplifiersBody(props: {
  rows: readonly AmplificationRow[];
  /** Where the foot link into the influence map points; none, no link. */
  campaignId?: string;
}): ReactNode {
  const largest = Math.max(
    0,
    ...props.rows.flatMap((row) => (row.reach === null ? [] : [row.reach])),
  );
  return (
    <>
      <ul className="flex flex-col">
        {props.rows.map((row) => {
          const name = row.amplifier_handle ?? pub('amp.handleAbsent');
          return (
            <li
              key={`${row.amplifier_account_id}-${row.kind}-${row.observed_at}`}
              className="flex items-center gap-3 border-t py-3 first:border-t-0"
            >
              <Avatar className="size-7" aria-hidden>
                <AvatarFallback>{initials(row.amplifier_handle ?? '')}</AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="text-body-sm truncate font-medium">{name}</span>
                {row.reach === null ? (
                  <div className="rounded-pill bg-secondary h-track w-full" />
                ) : (
                  <div
                    role="meter"
                    aria-label={pub('amp.reach', { name })}
                    aria-valuemin={0}
                    aria-valuemax={largest}
                    aria-valuenow={row.reach}
                    className="rounded-pill bg-secondary h-track w-full"
                  >
                    <div
                      data-slot="fill"
                      className="rounded-pill bg-primary h-track"
                      style={{ width: `${largest === 0 ? 0 : (row.reach / largest) * 100}%` }}
                    />
                  </div>
                )}
              </div>
              <span data-numeric className="text-caption shrink-0">
                {row.reach === null ? (
                  <Absent reason={{ text: pub('amp.noReach') }} />
                ) : (
                  compact(row.reach, getLocale())
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {props.campaignId !== undefined && (
        <Button asChild variant="ghost" size="sm" className="self-start">
          <Link to={overviewPath(props.campaignId)}>
            <ChevronRight aria-hidden />
            {pub('amp.map')}
          </Link>
        </Button>
      )}
    </>
  );
}
